/*  
  Developers: Sakshi Bhise  
  File: SignInHandler.tsx  
  Date: 06/18/2025  
  Project Name: AskHR  
  Team: DSET  
  
  Purpose:  
  Implements signing In Auth logic.  
  Handles the redirect logic after signin.   
*/

// ======================== Import Statements ======================== //  
// React hooks for state and side-effects  
import { useEffect, useState } from "react";  
// MSAL hook for Azure authentication  
import { useMsal } from "@azure/msal-react";  
// React Router hook for navigation  
import { useNavigate } from "react-router-dom";  
// Fluent UI Spinner and Stack for loading feedback and layout  
import { Spinner, SpinnerSize, Stack } from "@fluentui/react";  
// CSS module for chat-specific styles  
import styles from './chat/Chat.module.css';  
import { Loader } from "@teva/ui";
  
// ======================== Component ======================== //  
/**  
 * SigninHandler  
 * Handles authentication redirection logic and shows a spinner while processing.  
 */  
export default function SigninHandler() {  
  // MSAL instance for authentication actions  
  const { instance } = useMsal();  
  // React Router navigation function  
  const navigate = useNavigate();  
  // Local state to control spinner visibility  
  const [processing, setProcessing] = useState(true);  
  
  // Custom styles for Fluent UI Spinner  
  const spinnerStyles = {  
    label: {  
      fontSize: '16px',    // Larger label for readability  
    },  
    circle: {  
      width: 100,          // Large spinner size for emphasis  
      height: 100,  
      borderWidth: 6,      // Thicker border for better visibility  
    },  
  };  
  
  // ======================== Authentication Side Effect ======================== //  
  useEffect(() => {  
    let isMounted = true; // Prevents state updates if component unmounts  
  
    // Handles MSAL authentication redirect  
    const handleRedirectLogin = async () => {  
      try {  
        // Wait for MSAL redirect to resolve  
        const response = await instance.handleRedirectPromise();  
        if (isMounted) {  
          if (response) {  
            // If login successful, set active account for MSAL  
            instance.setActiveAccount(response.account);  
            console.log("Login successful, account set:", response.account);  
          }  
          // Navigate to main app regardless of result  
          navigate("/", { replace: true });  
        }  
      } catch (err) {  
        // Log error and optionally show error UI  
        console.error("MSAL Redirect Error:", err);  
        if (isMounted) {  
          navigate("/", { replace: true });  
        }  
      } finally {  
        // Hide spinner once complete  
        if (isMounted) setProcessing(false);  
      }  
    };  
  
    // Start login redirect handling  
    handleRedirectLogin();  
  
    // Cleanup function: prevents state updates after unmount  
    return () => {  
      isMounted = false;  
    };  
  }, [instance, navigate]);  
  
  // ======================== Render Loading UI ======================== //  
  return (  
    <div className={styles.container}>  
      {/* Stack centers spinner and message */}  
      <Stack className={styles.chatEmptyState}>  
        <Loader isLoading={true} loaderText='Authenticating...' imgWidth={400} isFullScreen={true} isOverlay={true} />
        {/*   
          .chatIconBlink (likely blinks or animates the text)  
          Color and animation details are in the CSS module:  
          - Usually dark text, e.g. #333, for strong contrast  
        */}  
      </Stack>  
    </div>  
  );  
}  